import React from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { getStartDate } from 'support/mediaTileHelper';
import { parseDate } from 'common/support/dateHelper';

import { ChannelKey } from 'app-types';
import AuthNWrapper, { WithAuthNProps } from 'components/hoc/AuthNWrapper';
import EventTitle from 'components/common/tile/EventTitle';
import { useNetworkName } from 'hooks/kitchenSink';

import MediaTile from './MediaTile';
import BrandedAuthTile from './BrandedAuthTile';
import styles from './LiveTile.module.scss';

const messages = {
  authText: {
    id: 'tile.auth.text',
    description: 'Auth text for Media Tile',
  },
  liveFlag: {
    id: 'tile.liveFlag',
    description: 'Text for live flag',
  },
  livePregameFlag: {
    id: 'tile.livePregameFlag',
    description: 'Text for live pregame flag',
  },
  livePostgameFlag: {
    id: 'tile.livePostgameFlag',
    description: 'Text for live postgame flag',
  },
  nowPlaying: {
    id: 'tile.nowPlaying',
    description: 'Text for now playing video flag',
  },
  ttsLive: {
    id: 'tile.tts.live',
    description: 'live',
  },
  ttsLivePregame: {
    id: 'tile.tts.livePregame',
    description: 'live pregame',
  },
  ttsLivePostgame: {
    id: 'tile.tts.livePostgame',
    description: 'live postgame',
  },
  gameAt: {
    id: 'tile.gameAt',
    description: 'Text for game at',
  },
};

interface LiveTileProps extends WithAuthNProps {
  prefix?: string | null;
  title?: string | null;
  brand?: ChannelKey | null;
  isNowPlaying?: boolean;
  startDate?: number | null;
  status?: string | null;
  src?: string | null;
  size: 'sm' | 'md' | 'lg';
  focusId?: string | number | null;
  onClick?: () => void;
  onFocusSet?: () => void;
  className?: string | null;
  autoSizeSrc?: boolean;
}

const LiveTile = ({
  prefix = null,
  title = null,
  brand = null,
  authenticated = false,
  authTypes = [],
  isNowPlaying = false,
  status = null,
  startDate = null,
  ...rest
}: LiveTileProps) => {
  const intl = useIntl();
  const isPregame = status === 'pre';
  const isPostgame = status === 'post';
  const dateString = getStartDate(intl, parseDate(startDate));

  const getLiveBadgeTTS = () => {
    if (isPregame) {
      return intl.formatMessage(messages.ttsLivePregame);
    }
    if (isPostgame) {
      return intl.formatMessage(messages.ttsLivePostgame);
    }
    return intl.formatMessage(messages.ttsLive);
  };

  const tts = [
    isNowPlaying ? intl.formatMessage(messages.nowPlaying) : null,
    !authenticated ? intl.formatMessage(messages.authText) : null,
    getLiveBadgeTTS(),
    isPregame ? intl.formatMessage(messages.gameAt) : null,
    isPregame ? dateString : null,
    title,
    useNetworkName(brand),
  ]
    .filter(text => text)
    .join(', ');

  const renderTitle = (hasFocus?: boolean) => (
    <EventTitle
      prefix={prefix}
      title={title}
      brand={brand}
      shouldScroll={hasFocus}
    />
  );

  const getBadgeText = () => {
    if (isPregame) {
      return intl.formatMessage(messages.livePregameFlag);
    }
    if (isPostgame) {
      return intl.formatMessage(messages.livePostgameFlag);
    }
    return intl.formatMessage(messages.liveFlag);
  };

  const renderMetadata = (hasFocus: boolean) => (
    <>
      <BrandedAuthTile
        hasFocus={hasFocus}
        brand={brand}
        authenticated={authenticated}
        authTypes={authTypes}
      />
      <div className={classNames('d-flex flex-row w-100', styles.badgeRow)}>
        <span className={styles.liveBadge} data-testid="live-badge">
          {getBadgeText()}
        </span>
        {isPregame && (
          <span className={styles.gameTime}>
            <span className={styles.staticText}>
              {intl.formatMessage(messages.gameAt)}
            </span>
            <span className={styles.startDate}>{dateString}</span>
          </span>
        )}
      </div>
    </>
  );

  return (
    <MediaTile
      renderTitle={renderTitle}
      renderMetadata={renderMetadata}
      isLive
      isNowPlaying={isNowPlaying}
      ttsText={tts}
      {...rest}
    />
  );
};

export default React.memo(AuthNWrapper(LiveTile));
